import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import clamp from 'lodash.clamp';

import { toSeconds } from 'js/editor/PlayerContext/timingHelpers';
import {
  DEFAULT_PIXELS_PER_SECOND,
  MAX_PIXELS_PER_SECOND,
  MIN_PIXELS_PER_SECOND
} from 'js/editor/Timeline/AdvancedTimeline/config/consts';
import { PlayheadConfig } from 'js/editor/Timeline/AdvancedTimeline/PlayheadCanvas';
import getScribeLengthMs from 'js/playback/helpers/getScribeLengthMs';

import { isEditorCloseAction, isUpdateScribeAction } from './reducerHelpers/actionValidators';

type AdvancedTimelineState = {
  playStartMarker: PlayheadConfig | null;
  scrollToTime: null | number;
  pixelsPerSecond: number;
};

const initialState: AdvancedTimelineState = {
  playStartMarker: null,
  scrollToTime: null,
  pixelsPerSecond: DEFAULT_PIXELS_PER_SECOND
};

export const clampPixelsPerSecond = (value: number) => {
  return clamp(Math.round(value), MIN_PIXELS_PER_SECOND, MAX_PIXELS_PER_SECOND);
};

const advancedTimelineSlice = createSlice({
  name: 'advancedTimeline',
  initialState,
  reducers: {
    setPlayStartMarker(state, action: PayloadAction<PlayheadConfig | null>) {
      state.playStartMarker = action.payload;
    },
    setScrollToTime(state, action: PayloadAction<number>) {
      state.scrollToTime = action.payload;
      state.pixelsPerSecond = MAX_PIXELS_PER_SECOND;
    },
    clearScrollToTime(state) {
      state.scrollToTime = null;
    },
    setPixelsPerSecond(state, action: PayloadAction<number>) {
      state.pixelsPerSecond = clampPixelsPerSecond(action.payload);
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(isUpdateScribeAction, (state, action) => {
        const { scribe } = action;

        const duration = toSeconds(getScribeLengthMs(scribe));
        if (state.playStartMarker && state.playStartMarker.time !== undefined) {
          // Ensure the play start marker time does not exceed the new project duration
          if (state.playStartMarker.time > duration) {
            state.playStartMarker.time = duration;
          }
        }
      })
      .addMatcher(isEditorCloseAction, state => {
        state.playStartMarker = null;
        state.scrollToTime = null;
      });
  }
});

export const { setPlayStartMarker, setScrollToTime, clearScrollToTime, setPixelsPerSecond } =
  advancedTimelineSlice.actions;

export default advancedTimelineSlice.reducer;
